"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import {
  ArrowLeft,
  HeartIcon,
  MoreVerticalIcon,
  Share2Icon,
  X,
} from "lucide-react";
import { useServiceRating } from "@/hooks/useServiceRating";
import api, { type Service } from "@/lib/api";
import WorkerAvatar from "./WorkerAvatar";
import ArtificialIntelligenceIcon from "./ArtificialIntelligenceIcon";
import PhoneAIIcon from "./PhoneAIIcon";
import LineSetupIcon from "./LineSetup";
import { useMobileDetails } from "@/hooks/useMobileDetails";
import { AeraButton } from "@/app/components/ui/AeraButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";

interface WorkerProfileHeaderProps {
  name: string;
  stats?: string;
  rating?: string;
  likes?: number;
  completedPurchasesCount?: number;
  thumbnail?: string;
  onNameChange?: (name: string) => void;
  isEditable?: boolean;
  creatorId?: string;
  serviceId?: string;
  showActionButtons?: boolean;
  isOnboardingComplete?: boolean;
  onOpenAgentChat?: () => void;
  onOpenTestChat?: () => void;
  activeTab?: "agent" | "test" | "line";
  // New props
  showBackButton?: boolean;
  mobileOnlyBackButton?: boolean;
  onBack?: () => void;
  isHired?: boolean;
  compact?: boolean;
}

export default function WorkerProfileHeader({
  name,
  rating,
  likes = 500,
  completedPurchasesCount,
  thumbnail,
  // onNameChange,
  // isEditable = false,
  creatorId,
  serviceId,
  showActionButtons = true,
  isOnboardingComplete = false,
  onOpenLineSetup,
  onOpenAgentChat,
  onOpenTestChat,
  activeTab,
  showBackButton,
  onBack,
  isHired,
  compact = false,
}: WorkerProfileHeaderProps & {
  onToggleSidebar?: () => void;
  onOpenLineSetup?: () => void;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  useMobileDetails();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Use useServiceRating hook instead of custom fetch
  const { rating: actualRating } = useServiceRating(creatorId, serviceId);

  // Favorite state management
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(likes);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  const unwrapServiceResponse = (response: unknown): Service | null => {
    if (!response || typeof response !== "object") return null;
    if ("service" in response)
      return (response as { service?: Service }).service ?? null;
    if ("data" in response)
      return (response as { data?: Service }).data ?? null;
    return response as Service;
  };

  useEffect(() => {
    if (!serviceId) return;

    let cancelled = false;

    (async () => {
      try {
        // Hydrate favorited state from localStorage (backend does not expose GET /favorite in this environment).
        if (typeof window !== "undefined") {
          const raw = localStorage.getItem(`service:${serviceId}:favorited`);
          if (!cancelled && (raw === "1" || raw === "0")) {
            setIsFavorited(raw === "1");
          }
        }

        // Fetch service details for count and possibly status (if backend includes it).
        const response = await api.services.getById(serviceId);
        const service = unwrapServiceResponse(response);
        if (cancelled || !service) return;

        if (typeof service.favoritesCount === "number") {
          setFavoritesCount(service.favoritesCount);
        }

        const s = service as unknown as Record<string, unknown>;
        const favorited =
          typeof s.isFavorited === "boolean"
            ? (s.isFavorited as boolean)
            : typeof s.favorited === "boolean"
              ? (s.favorited as boolean)
              : typeof s.isFavorite === "boolean"
                ? (s.isFavorite as boolean)
                : typeof s.favorite === "boolean"
                  ? (s.favorite as boolean)
                  : undefined;

        if (typeof favorited === "boolean") {
          setIsFavorited(favorited);
        }
      } catch (error) {
        console.warn("Failed to load favorite state:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [serviceId]);

  const handleToggleFavorite = async () => {
    if (!serviceId || isTogglingFavorite) return;

    const previousState = isFavorited;
    const previousCount = favoritesCount;

    // Optimistic update
    setIsFavorited(!isFavorited);
    setFavoritesCount((prevCount) =>
      isFavorited ? prevCount - 1 : prevCount + 1,
    );
    setIsTogglingFavorite(true);

    try {
      if (isFavorited) {
        await api.services.unfavorite(serviceId);
      } else {
        await api.services.favorite(serviceId);
      }
      try {
        if (typeof window !== "undefined") {
          localStorage.setItem(
            `service:${serviceId}:favorited`,
            !previousState ? "1" : "0",
          );
        }
      } catch {
        // ignore localStorage failures
      }
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
      // Revert on error
      setIsFavorited(previousState);
      setFavoritesCount(previousCount);
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const handleShareClick = () => {
    if (typeof window !== "undefined") {
      window.parent.postMessage(
        {
          type: "OPEN_SHARE_MODAL",
          props: {
            shareUrl: window.location.href, // Or construct specific URL
            title: name,
          },
        },
        "*",
      );
    }
  };

  // Format rating display
  const displayRating =
    actualRating !== null ? `★ ${actualRating.average}` : rating;
  const actionBtnClass = compact ? "min-w-8 h-8 gap-1!" : "min-w-9 h-9 gap-1!";
  const actionIconClass = compact ? "w-4 h-4" : "w-5 h-5";

  return (
    <div
      className={`flex h-full w-full items-center justify-between bg-aera-white ${
        compact ? "px-4" : "p-4"
      }`}
    >
      <div className="flex items-center gap-2 overflow-hidden">
        {showBackButton && (
          <AeraButton
            variant="transparent"
            size="sm"
            paddingSize="none"
            onClick={onBack ?? (() => router.push("/?view=discover"))}
            icon={<ArrowLeft size={20} className="text-aera-charcoal"/>}
            className={`min-w-9 h-9 gap-1! ${searchParams?.get("view") === "discover" ? "" : "md:hidden"}`}
          />
        )}
        <div
          className={`bg-aera-lightgray rounded-full flex items-center justify-center overflow-hidden shrink-0 ${
            compact ? "h-8 w-8" : "h-10 w-10 md:h-12 md:w-12"
          }`}
        >
          <WorkerAvatar
            serviceId={serviceId}
            name={name}
            thumbnail={thumbnail}
            size="w-full h-full"
            className="rounded-full"
            skipLoading
          />
        </div>
        <div className="overflow-hidden min-w-0">
          <div className="grid grid-cols-[auto_8px] items-center gap-2">
            <h1
              className={`font-semibold text-aera-slate truncate ${
                compact ? "text-sm" : "text-base md:text-lg"
              }`}
            >
              {name}
            </h1>
          </div>
          {(completedPurchasesCount !== undefined ||
            actualRating !== null ||
            displayRating) && (
            <div
              className={`flex items-center gap-2 text-aera-slate truncate ${
                compact ? "text-xs" : "text-xs md:text-sm"
              }`}
            >
              {completedPurchasesCount !== undefined && (
                <span>{completedPurchasesCount} handled</span>
              )}
              {displayRating && <span>{displayRating}</span>}
            </div>
          )}
        </div>
      </div>

      {showActionButtons && (
        <div className="flex items-center gap-2">
          {/* Desktop actions */}
          <div className="hidden md:flex items-center gap-2">
            {isOnboardingComplete && isHired ? (
              <>
                <AeraButton
                  variant="transparent"
                  size="sm"
                  paddingSize="none"
                  onClick={onOpenAgentChat}
                  icon={<ArtificialIntelligenceIcon className="w-6 h-6" />}
                  className={`${actionBtnClass} ${activeTab === "agent" ? "bg-aera-amberlight/20 border-aera-amberlight border hover:bg-aera-amberlight/30" : "hover:bg-gray-100 border border-transparent"}`}
                />
                <AeraButton
                  variant="transparent"
                  size="sm"
                  paddingSize="none"
                  onClick={onOpenLineSetup}
                  icon={<LineSetupIcon className="w-6 h-6" />}
                  title="Line Setup"
                  className={`${actionBtnClass} ${activeTab === "line" ? "bg-aera-amberlight/20 border-aera-amberlight border hover:bg-aera-amberlight/30" : "hover:bg-gray-100 border border-transparent"}`}
                />
                <AeraButton
                  variant="transparent"
                  size="sm"
                  paddingSize="none"
                  onClick={onOpenTestChat}
                  icon={<PhoneAIIcon className="w-6 h-6" />}
                  className={`${actionBtnClass} ${activeTab === "test" ? "bg-aera-amberlight/20 border-aera-amberlight border hover:bg-aera-amberlight/30" : "hover:bg-gray-100 border border-transparent"}`}
                />
              </>
            ) : null}

            <AeraButton
              variant="transparent"
              size="sm"
              paddingSize="none"
              onClick={handleToggleFavorite}
              disabled={!serviceId || isTogglingFavorite}
              className={`${actionBtnClass} ${isFavorited ? "text-aera-red hover:text-aera-red" : "text-aera-charcoal hover:text-aera-red"}`}
              icon={
                <HeartIcon
                  className={`${actionIconClass} transition-all ${isFavorited ? "fill-aera-red" : "fill-none"}`}
                />
              }
            >
              <span className={compact ? "text-xs" : "text-sm"}>{favoritesCount}</span>
            </AeraButton>

            {/* Share Button (hidden if hired) */}
            {!isHired && (
              <AeraButton
                variant="transparent"
                size="sm"
                paddingSize="none"
                onClick={handleShareClick}
                className={actionBtnClass}
                title="Share"
                icon={<Share2Icon className={actionIconClass} />}
              />
            )}
          </div>

          {/* Mobile dropdown actions */}
          <div className="md:hidden">
            <DropdownMenu open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <DropdownMenuTrigger asChild>
                <AeraButton
                  variant="transparent"
                  size="sm"
                  paddingSize="none"
                  className="min-w-9 h-9 gap-1!"
                  icon={
                    isMobileMenuOpen ? (
                      <X className="w-5 h-5" />
                    ) : (
                      <MoreVerticalIcon className="w-5 h-5" />
                    )
                  }
                  aria-label="เมนู"
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className="min-w-48">
                {isOnboardingComplete ? (
                  <>
                    <DropdownMenuItem
                      className="cursor-pointer flex items-center gap-2"
                      onClick={onOpenAgentChat}
                    >
                      <ArtificialIntelligenceIcon className="w-5 h-5" />
                      แชทกับเอเจนต์
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="cursor-pointer flex items-center gap-2"
                      onClick={onOpenLineSetup}
                    >
                      <LineSetupIcon className="w-5 h-5" />
                      Line Setup
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="cursor-pointer flex items-center gap-2"
                      onClick={onOpenTestChat}
                    >
                      <PhoneAIIcon className="w-5 h-5" />
                      ทดสอบแชท
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                ) : null}

                <DropdownMenuItem
                  className="cursor-pointer flex items-center gap-2"
                  onSelect={(e) => {
                    e.preventDefault(); // keep dropdown open
                    handleToggleFavorite();
                  }}
                  disabled={!serviceId || isTogglingFavorite}
                >
                  <HeartIcon
                    className={`w-4.5 h-4.5 ${isFavorited ? "fill-aera-red text-aera-red" : "fill-none text-aera-charcoal"}`}
                  />
                  {isFavorited ? "เลิกถูกใจ" : "ถูกใจ"}{" "}
                  <span className="ml-auto text-xs text-aera-gray">
                    {favoritesCount}
                  </span>
                </DropdownMenuItem>

                {!isHired && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="cursor-pointer flex items-center gap-2"
                      onClick={handleShareClick}
                    >
                      <Share2Icon className="w-4 h-4" />
                      แชร์
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}
    </div>
  );
}
